/**
 * Vercel-style showcase components for the landing page: the animated hero
 * emblem (pure SVG + SMIL — no JS), a miniature browser mockup of the public
 * passport page, a publish "terminal" card, and the bento cards. Everything
 * is hand-built HTML/CSS/SVG; no images, no libraries.
 */

const T = {
  tr: {
    urlLabel: "idpp.net/p/x7Kd…",
    verified: "Doğrulanmış",
    productName: "Organik Pamuk Tişört",
    kicker: "DİJİTAL ÜRÜN PASAPORTU · TEKSTİL",
    rows: ["Malzeme Kompozisyonu", "Bakım Talimatları", "Tedarik Zinciri", "Yaşam Döngüsü (LCA)"],
    term: {
      title: "idpp publish",
      cmd: "pasaport yayımla --urun ORG-TEE-1",
      lines: [
        "Uyum kontrolü · 100/100",
        "JSON-LD üretildi · Ed25519 imzalandı",
        "Sürüm v3 zincire eklendi",
        "EU DPP Registry · kayıtlı",
      ],
      out: "Public:",
    },
    ppTitle: "Çok dilli public sayfa",
    ppText: "TR · EN · DE — uygulamasız, 1.5 saniyede.",
    ppWords: ["DİJİTAL ÜRÜN PASAPORTU", "DIGITAL PRODUCT PASSPORT", "DIGITALER PRODUKTPASS", "PASAPORT"],
    lcaTitle: "LCA — metodolojisiyle",
    lcaText: "clca.app motorundan, veri seti beyanıyla.",
    lcaLines: [
      ["GWP_total", "0.31 kg CO₂e", "A1-A3"],
      ["WDP", "0.71 m³", "A1-A3"],
      ["PENRT", "1.30 MJ", "A4"],
    ] as const,
    lcaMethod: "EN 15804+A2 · ecoinvent 3.12",
  },
  en: {
    urlLabel: "idpp.net/p/x7Kd…",
    verified: "Verified",
    productName: "Organic Cotton Tee",
    kicker: "DIGITAL PRODUCT PASSPORT · TEXTILE",
    rows: ["Material Composition", "Care Instructions", "Supply Chain", "Life Cycle (LCA)"],
    term: {
      title: "idpp publish",
      cmd: "publish passport --product ORG-TEE-1",
      lines: [
        "Compliance check · 100/100",
        "JSON-LD built · Ed25519 signed",
        "Version v3 appended to chain",
        "EU DPP Registry · registered",
      ],
      out: "Public:",
    },
    ppTitle: "Multilingual public page",
    ppText: "TR · EN · DE — app-free, in 1.5 seconds.",
    ppWords: ["DIGITAL PRODUCT PASSPORT", "DİJİTAL ÜRÜN PASAPORTU", "DIGITALER PRODUKTPASS", "PASSPORT"],
    lcaTitle: "LCA — with methodology",
    lcaText: "From the clca.app engine, dataset declared.",
    lcaLines: [
      ["GWP_total", "0.31 kg CO₂e", "A1-A3"],
      ["WDP", "0.71 m³", "A1-A3"],
      ["PENRT", "1.30 MJ", "A4"],
    ] as const,
    lcaMethod: "EN 15804+A2 · ecoinvent 3.12",
  },
} as const;

type Lang = "tr" | "en";

/* ------------------------------------------------------------ hero emblem */

/** QR finder-pattern corner (nested squares) for the emblem chip. */
function Finder({ x, y }: { x: number; y: number }) {
  return (
    <g>
      <rect
        x={x}
        y={y}
        width="26"
        height="26"
        rx="4"
        fill="none"
        stroke="rgba(255,255,255,0.5)"
        strokeWidth="2.5"
      />
      <rect x={x + 8} y={y + 8} width="10" height="10" rx="2" fill="rgba(255,255,255,0.55)" />
    </g>
  );
}

/**
 * The animated hero centerpiece — an "eclipse-lit" QR chip: pulsing backlight,
 * a slowly rotating dashed orbit with a tracer dot, and a scan beam sweeping
 * the chip. All animation is SMIL inside the SVG; zero JavaScript.
 */
export function HeroEmblem() {
  return (
    <svg className="ld-emblem" viewBox="0 0 460 460" role="img" aria-label="Digital Product Passport">
      <defs>
        <radialGradient id="em-glow" cx="50%" cy="38%" r="55%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.85" />
          <stop offset="35%" stopColor="#bcd8ff" stopOpacity="0.32" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="em-beam" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6d6bf7" stopOpacity="0" />
          <stop offset="50%" stopColor="#6d6bf7" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#6d6bf7" stopOpacity="0" />
        </linearGradient>
        <clipPath id="em-clip">
          <rect x="150" y="150" width="160" height="160" rx="26" />
        </clipPath>
      </defs>

      {/* pulsing backlight (the eclipse) */}
      <circle cx="230" cy="212" r="168">
        <animate attributeName="opacity" values="0.55;1;0.55" dur="6s" repeatCount="indefinite" />
      </circle>
      <circle cx="230" cy="212" r="168" fill="url(#em-glow)">
        <animate attributeName="opacity" values="0.6;1;0.6" dur="6s" repeatCount="indefinite" />
      </circle>

      {/* rotating dashed orbit + tracer dot */}
      <g>
        <circle
          cx="230"
          cy="230"
          r="196"
          fill="none"
          stroke="rgba(255,255,255,0.18)"
          strokeWidth="1"
          strokeDasharray="3 9"
        />
        <circle cx="230" cy="34" r="4" fill="#6d6bf7" />
        <circle cx="230" cy="34" r="9" fill="none" stroke="rgba(109,107,247,0.4)" strokeWidth="1" />
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 230 230"
          to="360 230 230"
          dur="36s"
          repeatCount="indefinite"
        />
      </g>
      {/* counter-rotating inner orbit */}
      <g>
        <circle
          cx="230"
          cy="230"
          r="150"
          fill="none"
          stroke="rgba(255,255,255,0.10)"
          strokeWidth="1"
          strokeDasharray="2 12"
        />
        <circle cx="380" cy="230" r="2.6" fill="#8b89f9" />
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="360 230 230"
          to="0 230 230"
          dur="52s"
          repeatCount="indefinite"
        />
      </g>

      {/* the chip: dark slab silhouetted against the glow */}
      <rect
        x="150"
        y="150"
        width="160"
        height="160"
        rx="26"
        fill="#0a0a0a"
        stroke="rgba(255,255,255,0.28)"
        strokeWidth="1.2"
      />
      <Finder x={168} y={168} />
      <Finder x={266} y={168} />
      <Finder x={168} y={266} />
      {/* sparse data modules */}
      {(
        [
          [272, 272],
          [286, 286],
          [272, 296],
          [296, 272],
          [222, 176],
          [222, 196],
          [206, 226],
          [238, 226],
          [222, 246],
          [176, 226],
          [268, 226],
          [222, 284],
        ] as const
      ).map(([mx, my]) => (
        <rect key={`${mx}-${my}`} x={mx} y={my} width="9" height="9" rx="1.5" fill="rgba(255,255,255,0.34)" />
      ))}

      {/* scan beam sweeping inside the chip */}
      <g clipPath="url(#em-clip)">
        <rect x="150" width="160" height="46" fill="url(#em-beam)">
          <animate attributeName="y" values="120;300;120" dur="4.2s" repeatCount="indefinite" />
        </rect>
      </g>

      {/* verified tick under the chip */}
      <g>
        <circle cx="230" cy="352" r="13" fill="none" stroke="#8b89f9" strokeWidth="1.5" />
        <path
          d="M224 352l4.5 4.5 8-9"
          fill="none"
          stroke="#8b89f9"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <animate attributeName="opacity" values="0.5;1;0.5" dur="4.2s" repeatCount="indefinite" />
      </g>
    </svg>
  );
}

/* -------------------------------------------------- browser mockup (page) */

const CARE_GLYPHS = ["30°", "△", "◻", "○"];

/** Miniature of the real public passport page inside a dark browser frame. */
export function BrowserMock({ lang }: { lang: Lang }) {
  const t = T[lang];
  return (
    <div className="mock-browser">
      <div className="mb-bar">
        <i />
        <i />
        <i />
        <span className="mb-url">{t.urlLabel}</span>
      </div>
      <div className="mb-body">
        <div className="mb-kicker">{t.kicker}</div>
        <div className="mb-title-row">
          <span className="mb-title">{t.productName}</span>
          <span className="mb-verified">
            <i /> {t.verified}
          </span>
        </div>
        {t.rows.map((row, idx) => (
          <div className="mb-row" key={row}>
            <span className="mb-caret">▸</span>
            <span>{row}</span>
            {idx === 1 && (
              <span className="mb-care">
                {CARE_GLYPHS.map((g) => (
                  <em key={g}>{g}</em>
                ))}
              </span>
            )}
            {idx === 2 && (
              <svg className="mb-map" viewBox="0 0 64 20" aria-hidden="true">
                <polyline
                  points="4,14 22,8 40,12 58,5"
                  fill="none"
                  stroke="#6d6bf7"
                  strokeWidth="1.4"
                  strokeDasharray="3 3"
                />
                {[
                  [4, 14],
                  [22, 8],
                  [40, 12],
                  [58, 5],
                ].map(([cx, cy]) => (
                  <circle key={`${cx}`} cx={cx} cy={cy} r="2.4" fill="#6d6bf7" />
                ))}
              </svg>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------- terminal mockup */

export function TerminalMock({ lang }: { lang: Lang }) {
  const t = T[lang].term;
  return (
    <div className="mock-term">
      <div className="mt-bar">
        <span className="mt-tri">▲</span> {t.title}
      </div>
      <div className="mt-body">
        <div className="mt-cmd">
          <span>$</span> {t.cmd}
        </div>
        {t.lines.map((line) => (
          <div className="mt-line" key={line}>
            <span className="mt-check">✓</span> {line}
          </div>
        ))}
        <div className="mt-out">
          {t.out} <span>https://idpp.net/01/04012345678925</span>
          <i className="mt-cursor" />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ bento cards */

/** Vercel-Passport-style card: multilingual wordmark + rainbow edge. */
export function PassportBentoCard({ lang }: { lang: Lang }) {
  const t = T[lang];
  return (
    <div className="ld-bcard">
      <div className="pp-visual">
        <div className="pp-words">
          {t.ppWords.map((w, i) => (
            <span key={w} style={{ opacity: 1 - i * 0.22 }}>
              {w}
            </span>
          ))}
        </div>
        <div className="pp-rainbow" aria-hidden="true" />
      </div>
      <h3>{t.ppTitle}</h3>
      <p>{t.ppText}</p>
    </div>
  );
}

/** LCA metrics card — terminal-flavored indicator table. */
export function LcaBentoCard({ lang }: { lang: Lang }) {
  const t = T[lang];
  return (
    <div className="ld-bcard">
      <div className="lca-visual">
        {t.lcaLines.map(([code, value, module]) => (
          <div className="lca-line" key={code}>
            <span className="lca-code">{code}</span>
            <span className="lca-val">{value}</span>
            <span className="lca-mod">{module}</span>
          </div>
        ))}
        <div className="lca-method">{t.lcaMethod}</div>
      </div>
      <h3>{t.lcaTitle}</h3>
      <p>{t.lcaText}</p>
    </div>
  );
}
